import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

import axios from '../../../services/axios';
import Loading from '../../../components/Loading';

import { Container } from '../../../styles/layout/Container';
import {
   PageHeader,
   PageHeaderText,
   PageTitle,
   PageSubtitle,
} from '../../../styles/layout/PageHeader';
import {
   Section,
   SectionBody,
   SectionHeader,
   SectionTitle,
} from '../../../styles/layout/Section';
import { FormGrid } from '../../../styles/layout/FormGrid';
import { Field } from '../../../styles/forms/Field';
import { ActionBar, ActionBarRight } from '../../../styles/layout/ActionBar';

// ─── Estado inicial do formulário ─────────────────────────────────────────────

const EMPTY_FORM_STATE = {
   full_name: '',
   cpf: '',
   company_entity: '',
   employment_status: 'ATIVO',
   job_position_id: '',
   department_id: '',
   cost_center_id: '',
   city_location: '',
   hire_date: '',
   termination_date: '',
   base_salary: '',
   monthly_work_hours: 220,
   pix_key: '',
   whatsapp_number: '',
   email: '',
   birth_date: '',
   transportation_cost_type: '',
   has_meal_allowance: false,
   is_technician: false,
   is_salesperson: false,
};

// ─── Componente ───────────────────────────────────────────────────────────────

export default function EmployeeForm() {
   const { employeeId } = useParams();
   const navigate = useNavigate();

   // Se há employeeId na rota, estamos editando; caso contrário, cadastrando
   const isEditMode = Boolean(employeeId);
   const pageTitle = isEditMode
      ? 'Editar funcionário'
      : 'Cadastrar funcionário';
   const pageSubtitle = isEditMode
      ? 'Atualize os dados do colaborador abaixo'
      : 'Preencha os dados para registrar um novo colaborador';

   const [formData, setFormData] = useState(EMPTY_FORM_STATE);
   const [isLoading, setIsLoading] = useState(false);
   const [isSubmitting, setIsSubmitting] = useState(false);

   // ─── Dados auxiliares para os selects ────────────────────────────────────
   const [availableDepartments, setAvailableDepartments] = useState([]);
   const [availableJobPositions, setAvailableJobPositions] = useState([]);
   const [availableCostCenters, setAvailableCostCenters] = useState([]);

   // Carrega selects auxiliares e, no modo edição, pré-popula o formulário
   useEffect(() => {
      async function loadFormDependencies() {
         setIsLoading(true);
         try {
            // Sempre busca os três selects em paralelo;
            // no modo edição, adiciona o fetch do funcionário no mesmo Promise.all
            const auxiliaryRequests = [
               axios.get('/hr/departments'),
               axios.get('/hr/job-positions'),
               axios.get('/hr/cost-centers'),
            ];

            if (isEditMode) {
               auxiliaryRequests.push(axios.get(`/hr/employees/${employeeId}`));
            }

            const responses = await Promise.all(auxiliaryRequests);

            setAvailableDepartments(responses[0].data?.data?.departments ?? []);
            setAvailableJobPositions(
               responses[1].data?.data?.job_positions ?? []
            );
            setAvailableCostCenters(
               responses[2].data?.data?.cost_centers ?? []
            );

            if (isEditMode && responses[3]) {
               const employee = responses[3].data?.data?.employee;
               if (employee) {
                  setFormData({
                     full_name: employee.full_name ?? '',
                     cpf: employee.cpf ?? '',
                     company_entity: employee.company_entity ?? '',
                     employment_status: employee.employment_status ?? 'ATIVO',
                     job_position_id: employee.job_position_id ?? '',
                     department_id: employee.department_id ?? '',
                     cost_center_id: employee.cost_center_id ?? '',
                     city_location: employee.city_location ?? '',
                     hire_date: employee.hire_date?.substring(0, 10) ?? '',
                     termination_date:
                        employee.termination_date?.substring(0, 10) ?? '',
                     base_salary: employee.base_salary ?? '',
                     monthly_work_hours: employee.monthly_work_hours ?? 220,
                     pix_key: employee.pix_key ?? '',
                     whatsapp_number: employee.whatsapp_number ?? '',
                     email: employee.email ?? '',
                     birth_date: employee.birth_date?.substring(0, 10) ?? '',
                     transportation_cost_type:
                        employee.transportation_cost_type ?? '',
                     has_meal_allowance: Boolean(employee.has_meal_allowance),
                     is_technician: Boolean(employee.is_technician),
                     is_salesperson: Boolean(employee.is_salesperson),
                  });
               }
            }
         } catch (error) {
            const errorMessage =
               error?.response?.data?.errors?.[0] ??
               error?.message ??
               'Erro ao carregar dados do formulário.';
            toast.error(errorMessage);
         } finally {
            setIsLoading(false);
         }
      }

      loadFormDependencies();
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [employeeId]);

   // ─── Handlers ────────────────────────────────────────────────────────────

   function handleFieldChange(event) {
      const { name, value, type, checked } = event.target;
      setFormData((previousFormData) => ({
         ...previousFormData,
         [name]: type === 'checkbox' ? checked : value,
      }));
   }

   function validateForm() {
      if (!formData.full_name.trim()) {
         toast.error('Nome completo é obrigatório.');
         return false;
      }
      if (!formData.cpf.trim()) {
         toast.error('CPF é obrigatório.');
         return false;
      }
      if (!formData.company_entity) {
         toast.error('Empresa é obrigatória.');
         return false;
      }
      if (!formData.job_position_id) {
         toast.error('Cargo é obrigatório.');
         return false;
      }
      if (!formData.department_id) {
         toast.error('Departamento é obrigatório.');
         return false;
      }
      if (!formData.cost_center_id) {
         toast.error('Centro de custo é obrigatório.');
         return false;
      }
      if (!formData.hire_date) {
         toast.error('Data de admissão é obrigatória.');
         return false;
      }
      if (!formData.base_salary) {
         toast.error('Salário base é obrigatório.');
         return false;
      }
      return true;
   }

   async function handleFormSubmit(event) {
      event.preventDefault();
      if (!validateForm()) return;

      setIsSubmitting(true);
      try {
         // Campos opcionais vazios enviados como null — o backend trata corretamente
         const payload = {
            ...formData,
            // Se não está desligado, termination_date não deve ser enviado
            termination_date:
               formData.employment_status === 'DESLIGADO'
                  ? formData.termination_date || null
                  : null,
            city_location: formData.city_location || null,
            pix_key: formData.pix_key || null,
            whatsapp_number: formData.whatsapp_number || null,
            email: formData.email || null,
            birth_date: formData.birth_date || null,
            transportation_cost_type: formData.transportation_cost_type || null,
         };

         if (isEditMode) {
            await axios.put(`/hr/employees/${employeeId}`, payload);
            toast.success('Funcionário atualizado com sucesso!');
            navigate(`/hr/employees/${employeeId}`);
         } else {
            const response = await axios.post('/hr/employees', payload);
            const newEmployeeId = response.data?.data?.employee?.id;
            toast.success('Funcionário cadastrado com sucesso!');
            navigate(`/hr/employees/${newEmployeeId}`);
         }
      } catch (error) {
         console.log(error);
         const errorMessage =
            error?.response?.data?.errors?.[0] ??
            error?.response?.data?.message ?? 
            error?.message ??
            'Erro ao salvar funcionário.';
         toast.error(errorMessage);
      } finally {
         setIsSubmitting(false);
      }
   }

   function handleCancel() {
      if (isEditMode) {
         navigate(`/hr/employees/${employeeId}`);
      } else {
         navigate('/hr/employees');
      }
   }

   // ─── Render ──────────────────────────────────────────────────────────────

   return (
      <Container>
         <Loading isLoading={isLoading} />

         <PageHeader>
            <PageHeaderText>
               <PageTitle>{pageTitle}</PageTitle>
               <PageSubtitle>{pageSubtitle}</PageSubtitle>
            </PageHeaderText>
         </PageHeader>

         <form onSubmit={handleFormSubmit}>
            {/* ─── Dados pessoais ───────────────────────────────────── */}
            <Section>
               <SectionHeader>
                  <SectionTitle>Dados pessoais</SectionTitle>
               </SectionHeader>
               <SectionBody>
                  <FormGrid $cols={12} $colsMobile={12}>
                     <Field $col={6} $colMobile={12}>
                        <label htmlFor="field-full-name">Nome completo *</label>
                        <input
                           id="field-full-name"
                           type="text"
                           name="full_name"
                           value={formData.full_name}
                           onChange={handleFieldChange}
                           placeholder="Nome completo do colaborador"
                        />
                     </Field>

                     <Field $col={3} $colMobile={6}>
                        <label htmlFor="field-cpf">CPF *</label>
                        <input
                           id="field-cpf"
                           type="text"
                           name="cpf"
                           value={formData.cpf}
                           onChange={handleFieldChange}
                           placeholder="000.000.000-00"
                           maxLength={14}
                           disabled={isEditMode} // ← adicionar
                           style={
                              isEditMode
                                 ? { opacity: 0.6, cursor: 'not-allowed' }
                                 : {}
                           }
                        />
                     </Field>

                     <Field $col={3} $colMobile={6}>
                        <label htmlFor="field-birth-date">
                           Data de nascimento
                        </label>
                        <input
                           id="field-birth-date"
                           type="date"
                           name="birth_date"
                           value={formData.birth_date}
                           onChange={handleFieldChange}
                        />
                     </Field>

                     <Field $col={4} $colMobile={12}>
                        <label htmlFor="field-whatsapp-number">WhatsApp</label>
                        <input
                           id="field-whatsapp-number"
                           type="text"
                           name="whatsapp_number"
                           value={formData.whatsapp_number}
                           onChange={handleFieldChange}
                           placeholder="(62) 99999-9999"
                        />
                     </Field>

                     <Field $col={4} $colMobile={12}>
                        <label htmlFor="field-email">E-mail</label>
                        <input
                           id="field-email"
                           type="email"
                           name="email"
                           value={formData.email}
                           onChange={handleFieldChange}
                           placeholder="email@exemplo.com"
                        />
                     </Field>

                     <Field $col={4} $colMobile={12}>
                        <label htmlFor="field-pix-key">Chave PIX</label>
                        <input
                           id="field-pix-key"
                           type="text"
                           name="pix_key"
                           value={formData.pix_key}
                           onChange={handleFieldChange}
                           placeholder="CPF, e-mail, telefone ou chave aleatória"
                        />
                     </Field>
                  </FormGrid>
               </SectionBody>
            </Section>

            {/* ─── Dados contratuais ────────────────────────────────── */}
            <Section>
               <SectionHeader>
                  <SectionTitle>Dados contratuais</SectionTitle>
               </SectionHeader>
               <SectionBody>
                  <FormGrid $cols={12} $colsMobile={12}>
                     <Field $col={12} $colMobile={12}>
                        <label htmlFor="field-job-position">Cargo *</label>
                        <select
                           id="field-job-position"
                           name="job_position_id"
                           value={formData.job_position_id}
                           onChange={handleFieldChange}
                        >
                           <option value="">Selecione...</option>
                           {availableJobPositions.map((jobPosition) => (
                              <option
                                 key={jobPosition.id}
                                 value={jobPosition.id}
                              >
                                 {jobPosition.position_name}
                              </option>
                           ))}
                        </select>
                     </Field>

                     <Field $col={4} $colMobile={6}>
                        <label htmlFor="field-company-entity">Empresa *</label>
                        <select
                           id="field-company-entity"
                           name="company_entity"
                           value={formData.company_entity}
                           onChange={handleFieldChange}
                        >
                           <option value="">Selecione...</option>
                           <option value="MYSHOP COMERCIO E SERVICOS LTDA">
                              MYSHOP COMERCIO E SERVICOS LTDA
                           </option>
                           <option value="LIFE PURIFICADORES LTDA">
                              LIFE PURIFICADORES LTDA
                           </option>
                        </select>
                     </Field>

                     <Field $col={4} $colMobile={6}>
                        <label htmlFor="field-employment-status">
                           Status *
                        </label>
                        <select
                           id="field-employment-status"
                           name="employment_status"
                           value={formData.employment_status}
                           onChange={handleFieldChange}
                        >
                           <option value="ATIVO">Ativo</option>
                           <option value="EXP 1">Experiência 1 - 30d</option>
                           <option value="EXP 2">Experiência 2 - 60d</option>
                           <option value="FÉRIAS">Férias</option>
                           <option value="DESLIGADO">Desligado</option>
                        </select>
                     </Field>

                     <Field $col={4} $colMobile={12}>
                        <label htmlFor="field-department">Departamento *</label>
                        <select
                           id="field-department"
                           name="department_id"
                           value={formData.department_id}
                           onChange={handleFieldChange}
                        >
                           <option value="">Selecione...</option>
                           {availableDepartments.map((department) => (
                              <option key={department.id} value={department.id}>
                                 {department.department_name}
                              </option>
                           ))}
                        </select>
                     </Field>

                     <Field $col={4} $colMobile={12}>
                        <label htmlFor="field-cost-center">
                           Centro de custo *
                        </label>
                        <select
                           id="field-cost-center"
                           name="cost_center_id"
                           value={formData.cost_center_id}
                           onChange={handleFieldChange}
                        >
                           <option value="">Selecione...</option>
                           {availableCostCenters.map((costCenter) => (
                              <option key={costCenter.id} value={costCenter.id}>
                                 {costCenter.cost_center_code} —{' '}
                                 {costCenter.cost_center_name}
                              </option>
                           ))}
                        </select>
                     </Field>

                     <Field $col={4} $colMobile={6}>
                        <label htmlFor="field-city-location">
                           Praça / Cidade
                        </label>
                        <input
                           id="field-city-location"
                           type="text"
                           name="city_location"
                           value={formData.city_location}
                           onChange={handleFieldChange}
                           placeholder="Ex: Goiânia"
                        />
                     </Field>

                     <Field $col={3} $colMobile={6}>
                        <label htmlFor="field-hire-date">
                           Data de admissão *
                        </label>
                        <input
                           id="field-hire-date"
                           type="date"
                           name="hire_date"
                           value={formData.hire_date}
                           onChange={handleFieldChange}
                        />
                     </Field>

                     {/* Data de desligamento aparece apenas no modo edição */}
                     {isEditMode && (
                        <Field $col={3} $colMobile={6}>
                           <label htmlFor="field-termination-date">
                              Data de desligamento
                           </label>
                           <input
                              id="field-termination-date"
                              type="date"
                              name="termination_date"
                              value={formData.termination_date}
                              onChange={handleFieldChange}
                              disabled={
                                 formData.employment_status !== 'DESLIGADO'
                              }
                              style={
                                 formData.employment_status !== 'DESLIGADO'
                                    ? { opacity: 0.6, cursor: 'not-allowed' }
                                    : {}
                              }
                           />
                        </Field>
                     )}

                     <Field $col={3} $colMobile={6}>
                        <label htmlFor="field-base-salary">
                           Salário base (R$) *
                        </label>
                        <input
                           id="field-base-salary"
                           type="number"
                           name="base_salary"
                           value={formData.base_salary}
                           onChange={handleFieldChange}
                           placeholder="0,00"
                           step="0.01"
                           min="0"
                        />
                     </Field>

                     <Field $col={3} $colMobile={6}>
                        <label htmlFor="field-monthly-work-hours">
                           Carga horária mensal (h) *
                        </label>
                        <input
                           id="field-monthly-work-hours"
                           type="number"
                           name="monthly_work_hours"
                           value={formData.monthly_work_hours}
                           onChange={handleFieldChange}
                           min="1"
                        />
                     </Field>

                     <Field $col={3} $colMobile={12}>
                        <label htmlFor="field-transportation-cost-type">
                           Tipo de transporte
                        </label>
                        <select
                           id="field-transportation-cost-type"
                           name="transportation_cost_type"
                           value={formData.transportation_cost_type}
                           onChange={handleFieldChange}
                        >
                           <option value="">Selecione...</option>
                           <option value="AUXÍLIO COMBUSTÍVEL">
                              Auxílio combustível
                           </option>
                           <option value="VALE TRANSPORTE">
                              Vale transporte
                           </option>
                           <option value="NENHUM">nenhum</option>
                           <option value="NAO SE APLICA">Não se aplica</option>
                        </select>
                     </Field>

                     <Field $col={4} $colMobile={4}>
                        <label htmlFor="field-has-meal-allowance">&nbsp;</label>
                        <label
                           htmlFor="field-has-meal-allowance"
                           style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '8px',
                              cursor: 'pointer',
                           }}
                        >
                           <input
                              id="field-has-meal-allowance"
                              type="checkbox"
                              name="has_meal_allowance"
                              style={{ height: '16px', width: '16px' }}
                              checked={formData.has_meal_allowance}
                              onChange={handleFieldChange}
                           />
                           Recebe vale alimentação
                        </label>
                     </Field>

                     <Field $col={4} $colMobile={4}>
                        <label htmlFor="field-is-technician">&nbsp;</label>
                        <label
                           htmlFor="field-is-technician"
                           style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '8px',
                              cursor: 'pointer',
                           }}
                        >
                           <input
                              id="field-is-technician"
                              type="checkbox"
                              style={{ height: '16px', width: '16px' }}
                              name="is_technician"
                              checked={formData.is_technician}
                              onChange={handleFieldChange}
                           />
                           É técnico de campo
                        </label>
                     </Field>

                     <Field $col={4} $colMobile={4}>
                        <label htmlFor="field-is-salesperson">&nbsp;</label>
                        <label
                           htmlFor="field-is-salesperson"
                           style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '8px',
                              cursor: 'pointer',
                           }}
                        >
                           <input
                              id="field-is-salesperson"
                              type="checkbox"
                              style={{ height: '16px', width: '16px' }}
                              name="is_salesperson"
                              checked={formData.is_salesperson}
                              onChange={handleFieldChange}
                           />
                           É vendedor
                        </label>
                     </Field>

                     {/* Ações dentro do último Field do form — padrão do DS */}
                     <Field $col={12} $colMobile={12} $alignEnd>
                        <label>&nbsp;</label>
                        <ActionBar>
                           <ActionBarRight>
                              <button
                                 type="button"
                                 onClick={handleCancel}
                                 disabled={isSubmitting}
                              >
                                 Cancelar
                              </button>
                              <button type="submit" disabled={isSubmitting}>
                                 {/* eslint-disable-next-line no-nested-ternary */}
                                 {isSubmitting
                                    ? 'Salvando...'
                                    : isEditMode
                                      ? 'Salvar alterações'
                                      : 'Cadastrar funcionário'}
                              </button>
                           </ActionBarRight>
                        </ActionBar>
                     </Field>
                  </FormGrid>
               </SectionBody>
            </Section>
         </form>
      </Container>
   );
}
